import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Sparkles, Coffee, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending_timeout | error
  const [type, setType] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setType(res.data.type);
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      if (attempts.current >= 14) { setState("pending_timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-32 pb-16 px-4 sm:px-6 max-w-lg mx-auto text-center">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <div className="w-10 h-10 border-2 border-brand-accent border-t-transparent rounded-full animate-spin mx-auto mb-6" />
            <h1 className="font-cormorant text-3xl font-semibold text-foreground mb-2">Confirming your payment...</h1>
            <p className="font-manrope text-sm text-muted-foreground">This usually takes just a moment.</p>
          </div>
        )}

        {state === "paid" && (
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} data-testid="payment-success">
            <CheckCircle2 size={52} className="text-green-600 mx-auto mb-5" />
            {type === "tip" ? (
              <>
                <h1 className="font-cormorant text-4xl font-semibold text-foreground mb-3">Thank you for the tip!</h1>
                <p className="font-manrope text-sm text-muted-foreground mb-8">
                  Your support means the world and helps keep this newsletter going.
                </p>
                <Link to="/" data-testid="payment-home-btn" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors">
                  <Coffee size={16} /> Back to reading
                </Link>
              </>
            ) : (
              <>
                <h1 className="font-cormorant text-4xl font-semibold text-foreground mb-3">Welcome to Premium!</h1>
                <p className="font-manrope text-sm text-muted-foreground mb-8">
                  Your membership is active — every premium post is now unlocked for you.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/" data-testid="payment-read-btn" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-brand-accent text-white font-manrope text-sm font-medium hover:bg-brand-accent-hover transition-colors">
                    <Sparkles size={16} /> Start reading
                  </Link>
                  <Link to="/referrals" className="inline-flex items-center justify-center px-6 py-3 rounded-full border border-border font-manrope text-sm font-medium text-foreground hover:bg-muted transition-colors">
                    Refer friends
                  </Link>
                </div>
              </>
            )}
          </motion.div>
        )}

        {state === "pending_timeout" && (
          <div data-testid="payment-pending">
            <AlertCircle size={44} className="text-brand-accent mx-auto mb-5" />
            <h1 className="font-cormorant text-3xl font-semibold text-foreground mb-3">Payment is processing</h1>
            <p className="font-manrope text-sm text-muted-foreground mb-8">
              Your payment is still being confirmed. Check your email for a receipt — your access
              will activate automatically once it clears.
            </p>
            <Link to="/" className="font-manrope text-sm text-brand-accent hover:underline">Back to home</Link>
          </div>
        )}

        {state === "error" && (
          <div data-testid="payment-error">
            <AlertCircle size={44} className="text-destructive mx-auto mb-5" />
            <h1 className="font-cormorant text-3xl font-semibold text-foreground mb-3">Something went wrong</h1>
            <p className="font-manrope text-sm text-muted-foreground mb-8">
              We couldn't confirm this payment. If you were charged, contact hello@freelanceblogger.club.
            </p>
            <Link to="/subscribe" className="font-manrope text-sm text-brand-accent hover:underline">Try again</Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
